import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

/**
 * Catches backend OAuth redirects like:
 *   /oauth-error?provider=google&status=error
 *   /oauth-success?provider=google
 *
 * The applicant flow lives at /verify/:token, but the backend doesn't always
 * know that token at redirect time. We stash it in sessionStorage before
 * starting the OAuth handoff (see ApplicantFlow.startOAuth) and use it here
 * to route the applicant back to their session with `?oauth=&status=` query
 * params, which ApplicantFlow turns into a corner toast.
 *
 * If we have no stashed token (e.g., user opened the URL in a new browser),
 * we fall back to the demo applicant flow with the same query params so the
 * user still sees a friendly toast instead of a 404.
 */
export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const provider = searchParams.get("provider") ?? "account";
    // Default the status based on which path we landed on. The route element
    // can pass an explicit status via the URL too.
    const explicitStatus = searchParams.get("status");
    const inferredStatus = window.location.pathname.includes("oauth-error") ? "error" : "success";
    const status = explicitStatus ?? inferredStatus;

    let token: string | null = null;
    let step: string | null = null;
    try {
      token = sessionStorage.getItem("dv_oauth_return_token");
      step = sessionStorage.getItem("dv_oauth_return_step");
      sessionStorage.removeItem("dv_oauth_return_token");
      sessionStorage.removeItem("dv_oauth_return_step");
    } catch {
      /* ignore storage errors */
    }

    const params = new URLSearchParams({ oauth: provider, status });
    if (step) params.set("step", step);
    const target = token
      ? `/verify/${token}?${params.toString()}`
      : `/demo/applicant-flow?${params.toString()}`;

    navigate(target, { replace: true });
  }, [navigate, searchParams]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Returning to your application…</span>
      </div>
    </div>
  );
}
